import { Color } from "game-common/character/character"

// Look and metrics for the character-creation screen. Kept free of pixi imports so the layout and
// trait modules that use it stay unit-testable (see the import-cycle note in the gui tests).
//
// The palette is the ember-lit one the rest of the game's newer UI uses (command wheel, recruit role
// picker): warm amber rim over dark glass.

export const RIM = 0xf2a94e
export const RIM_DIM = 0x6b4f38
export const BACKDROP = 0x120d0a
export const PANEL_FILL = 0x1b1512
export const PANEL_ALPHA = 0.92
export const CARD_FILL = 0x2a201a
export const CARD_HOVER_FILL = 0x3a2c21
export const CARD_RIM = 0x54402e
export const TEXT = 0xfff3e0
export const TEXT_DIM = 0xb49b83
export const ACCENT = 0xffd27a
export const SELECTED_FILL = 0x4a3113

// The modal draws into an 800x442 logical screen and Dialog insets the content by the modal's
// padding (10), so the base content coordinates are 0..780 across, 0..420 down. Wide mobile
// canvases add their extra width to the palette. Ending by 420 keeps the footer clear of the floor.
export let CONTENT_W = 780
export const CONTENT_H = 420

export const HEADER_H = 34
export const FOOTER_H = 42
export const BODY_Y = HEADER_H + 10
export const BODY_H = CONTENT_H - FOOTER_H - BODY_Y

export const COL_GAP = 8
export const TRAITS_W = 176
export const STAGE_W = 232
export let PALETTE_W = CONTENT_W - TRAITS_W - STAGE_W - 2 * COL_GAP

export const TRAITS_X = 0
export const STAGE_X = TRAITS_W + COL_GAP
export const PALETTE_X = STAGE_X + STAGE_W + COL_GAP

/** Reflow the forge's palette into the extra mobile width; keep the preview and trait rows stable. */
export const setContentWidth = (width: number) => {
    CONTENT_W = width
    PALETTE_W = CONTENT_W - TRAITS_W - STAGE_W - 2 * COL_GAP
}

// Representative colours for the swatch grids, sampled from the LPC sheets themselves (the most
// common mid-tone of body/male/human/*.png, hair/plain/male/*.png and feet/boots/male/*.png) so a
// swatch actually looks like the thing it paints on the character. "white" skin, "white" hair and a
// "white" tunic are three very different colours, hence three maps rather than one.
export const SKIN_SWATCH: Partial<Record<Color, number>> = {
    white: 0xf9d5ba,
    light: 0xfdd5b7,
    tan: 0xfdd082,
    olive: 0xd98e60,
    dark: 0xba8454,
    brown: 0xa86431,
    black: 0x61382d,
}

export const HAIR_SWATCH: Partial<Record<Color, number>> = {
    white: 0xd8dcdc,
    blonde: 0xfccf56,
    gold: 0xe8c31a,
    green: 0x2f8f2f,
    blue: 0x2f7fcb,
    red: 0xe21414,
    orange: 0xe55600,
    purple: 0xa966dd,
    brown: 0x6d2f0d,
    lightbrown: 0xae682a,
    gray: 0xaaaaaa,
    black: 0x31313e,
}

// Cloth/leather colours. Sampled from the boot sheets, which carry the widest palette; the handful
// of colours no garment sheet has (armour tints, and the odd colour that only hair or skin uses)
// are reasonable stand-ins.
export const CLOTH_SWATCH: Partial<Record<Color, number>> = {
    black: 0x4a5057,
    blue: 0x61a0ef,
    brown: 0x996b4a,
    gray: 0xa2a0a4,
    green: 0x64a42c,
    maroon: 0xae424a,
    orange: 0xef7e19,
    purple: 0x813089,
    red: 0xcd2429,
    tan: 0xb78c41,
    teal: 0x00cfdf,
    white: 0xe5e6c7,
    yellow: 0xf3c03f,
    gold: 0xe0b23c,
    metal: 0xc9ccd6,
    silver: 0xd9dde3,
    olive: 0x6b6b32,
    lightbrown: 0x9b6b3a,
    lightgreen: 0x8ed07a,
    light: 0xe8e0d0,
    dark: 0x3b3b44,
    pink: 0xe89bbd,
    blonde: 0xe7c98a,
    na: 0x808080,
}

export type SwatchFamily = "skin" | "hair" | "cloth"

const FAMILY_MAP: Record<SwatchFamily, Partial<Record<Color, number>>> = {
    skin: SKIN_SWATCH,
    hair: HAIR_SWATCH,
    cloth: CLOTH_SWATCH,
}

// A swatch always has to paint something: fall back through the cloth table (the broadest) to a
// neutral grey rather than drawing an invisible hole in the palette.
export const swatchFor = (family: SwatchFamily, color: Color): number =>
    FAMILY_MAP[family][color] ?? CLOTH_SWATCH[color] ?? 0x808080

// Hand-written names for the values whose raw ids are either unreadable or far too long for a chip.
const NICE_NAMES: Record<string, string> = {
    none: "None",
    "pants-armor": "Armored",
    "shoes-armor": "Armored",
    skirts: "Skirt",
    scoop_shirt: "Scoop Neck",
    blouse_longsleeve: "Long Blouse",
    longsleeve_laced: "Laced",
    sleeveless_laced: "Laced Vest",
    sleeveless_striped: "Striped",
    bob_side_part: "Side Bob",
    curly_long: "Long Curls",
    curly_short: "Short Curls",
    curtains_long: "Long Curtains",
    dreadlocks_long: "Long Dreads",
    dreadlocks_short: "Dreads",
    flat_top_fade: "Flat Fade",
    flat_top_straight: "Flat Top",
    high_and_tight: "High & Tight",
    high_ponytail: "High Tail",
    long_center_part: "Center Part",
    long_messy: "Messy Long",
    long_messy2: "Messy Long 2",
    spiked_beehive: "Beehive",
    spiked_liberty: "Liberty",
    spiked_liberty2: "Liberty 2",
    spiked_porcupine: "Porcupine",
    twists_straight: "Twists",
    twists_fade: "Twist Fade",
    lightbrown: "Light Brown",
    lightgreen: "Light Green",
    half_up: "Half Up",
    bangs_bun: "Bangs Bun",
}

// "flat_top_fade" -> "Flat Top Fade", "bangslong2" -> "Bangslong 2".
export const prettyLabel = (value?: string): string => {
    if (!value) {
        return "None"
    }
    const nice = NICE_NAMES[value]
    if (nice) {
        return nice
    }
    return value
        .split(/[-_]/)
        .map(word => word.replace(/(\d+)$/, " $1"))
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
}
